import React, { useState, useContext, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { TbChecklist } from "react-icons/tb";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import { toast } from "react-toastify";
import { showSuccess, showError } from "../../../utils/toast";
import ButtonUI from "../../material-ui/ButtonUI";
import { AuthContext } from "../../../context/Auth/AuthContext";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import assignUser from "../../../api/assignUser";
import "../../../styles/AssignUserPopup.css";
import getAllAssignUsers from "../../../api/getAllAssignUsers";
import CircularProgress from "@mui/material/CircularProgress";

const rejectCaseFormSchema = yup.object().shape({
  user: yup.string().required("User is required field"),
});

const AssignUserPopup = ({
  selectedRows,
  setDocumentUpdatedToTrue,
  bulkAssign,
  setBulkAssign,
  caseList,
  createdAt,
  documentSourceDetails,
  setRowSelection,
  bulkAssignIds,
}) => {
  const { token } = useContext(AuthContext);

  const [userList, setUserList] = useState([]);
  const [hasFetchedUsers, setHasFetchedUsers] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(rejectCaseFormSchema),
  });

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    ignoreCase: true, // Ignore case sensitivity
    stringify: (option) => option.user_name,
  });

  const changeDropdownVisibilityFunc = () => {
    setBulkAssign(!bulkAssign);
  };

  const handleClose = () => {
    setBulkAssign(false);
  };

  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      setHasFetchedUsers(false); // optional: reset when reopening
      const response = await getAllAssignUsers({ token });
      setUserList(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      setUserList([]);
      toast.error("Failed to load users", {
        position: "top-center",
        autoClose: 3000,
        theme: "dark",
      });
    } finally {
      setUsersLoading(false);
      setHasFetchedUsers(true); // ✅ mark fetch completed
    }
  };

  // ✅ Fetch only when popup opens
  useEffect(() => {
    if (bulkAssign) fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkAssign]);

  const onAssignUserFormSubmit = async (assignUserFormData) => {
    const assignUserFormResponse = await assignUser({
      token: token,
      document_source_details: documentSourceDetails,
      created_at: createdAt,
      document_source_type: caseList,
      user_id: assignUserFormData.user,
      bulkAssignIds: bulkAssignIds,
    });

    if (assignUserFormResponse.code === 200) {
      setBulkAssign(false);
      setDocumentUpdatedToTrue();
      setRowSelection({});
      changeDropdownVisibilityFunc();
      showSuccess("Document is assigned successfully");
    } else {
      showError("Error while assiging document/s to an user");
    }
  };

  const isReady = !usersLoading && userList.length > 0;

  return (
    <Dialog
      open={bulkAssign}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="alert-dialog-title" className="edit-doc-modal">
        <div className="popup-header-svg-icon">
          <TbChecklist />
        </div>
        <div className="popup-header-title">Assign User</div>
      </DialogTitle>
      <DialogContent>
        {/* <DialogContentText id="alert-dialog-description"> */}
        <div style={{ display: "flex", flexDirection: "column" }}>
          <FormControl fullWidth>
            <Controller
              control={control}
              name="user"
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  options={userList}
                  loading={usersLoading}
                  disabled={!isReady}
                  getOptionLabel={(option) => {
                    // Find all users with the same user_name
                    const duplicates = userList.filter(
                      (item) => item.user_name === option.user_name,
                    );

                    // If there are duplicates, find the index of the current option
                    const index = duplicates.findIndex(
                      (item) => item.id === option.id,
                    );

                    // Return the user_name with a sequential number for duplicates
                    return duplicates.length > 1
                      ? `${option.user_name} (${index + 1})`
                      : option.user_name;
                  }}
                  filterOptions={filterOptions}
                  onChange={(e, value) => {
                    onChange(value ? value.id : "");
                  }}
                  sx={{ margin: "10px 0px" }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={usersLoading ? "Loading users..." : "User"}
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {usersLoading ? (
                              <CircularProgress size={18} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            />
          </FormControl>

          {hasFetchedUsers && !usersLoading && userList.length === 0 && (
            <span className="error-message">No users found to assign.</span>
          )}

          <span className="error-message">{errors["user"]?.message}</span>
        </div>
        {/* </DialogContentText> */}
      </DialogContent>
      <DialogActions style={{ justifyContent: "flex-end" }}>
        <ButtonUI
          formButton={false}
          variant="outlined"
          buttonText="No"
          style={{
            margin: "15px 0px",
            marginTop: "0px",
            backgroundColor: "#fff",
            color: "#1a6b7c",
            border: "1px solid #1a6b7c",
          }}
          onClickFunc={handleClose}
        />
        <ButtonUI
          variant="outlined"
          buttonText="Yes"
          style={{
            margin: "15px",
            marginTop: "0px",
            backgroundColor: "#fff",
            color: "#1a6b7c",
            border: "1px solid #1a6b7c",
          }}
          autoFocus={true}
          disabled={!isReady} // ✅ don't allow submit until users loaded
          handleSubmit={handleSubmit}
          onFormSubmit={onAssignUserFormSubmit}
        />
      </DialogActions>
    </Dialog>
  );
};

export default AssignUserPopup;
